"use client";

import Link from "next/link";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { BrandLockup } from "@/components/brand-lockup";
import { LanguageSwitcher } from "@/components/language-switcher";
import { localePath, type Locale } from "@/i18n/config";
import { dictionaryFor } from "@/i18n/dictionaries";

// "Get the app" uses the smart download link: Android goes to Google Play, iPhone to the App
// Store, and desktop to a page with both badges.
const getAppHref = "/download";

// The download link is a route handler redirect, so it is a plain anchor (no client navigation or prefetch).
function NavAnchor({ action, ...props }: React.ComponentProps<typeof Link> & { action: boolean }) {
  const { href, children, prefetch: _prefetch, ...rest } = props;
  void _prefetch;
  return action
    ? <a href={String(href)} {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>{children}</a>
    : <Link href={href} {...rest}>{children}</Link>;
}

export function SiteHeader({ locale = "en" }: { locale?: Locale }) {
  const copy = dictionaryFor(locale).global;
  const primaryNavigation = [
    { href: `${localePath(locale, "/")}#how-it-works`, kind: "link", label: copy.nav.howItWorks },
    { href: localePath(locale, "/about"), kind: "link", label: copy.nav.about },
    { href: getAppHref, kind: "action", label: copy.nav.getApp },
  ] as const;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const openButtonRef = useRef<HTMLButtonElement>(null);

  const closeMenu = useCallback((restoreFocus = true) => {
    setIsMenuOpen(false);
    if (restoreFocus) openButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu();
        return;
      }

      if (event.key !== "Tab" || !drawerRef.current) return;

      const focusable = Array.from(
        drawerRef.current.querySelectorAll<HTMLElement>("a[href], button:not([disabled])"),
      );
      const first = focusable.at(0);
      const last = focusable.at(-1);

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeMenu, isMenuOpen]);

  return (
    <header className="site-header">
      <div className="site-header__inner">
        <Link className="site-header__brand" href={localePath(locale, "/")} aria-label={copy.nav.brandLabel}>
          <BrandLockup priority />
        </Link>

        <nav className="desktop-nav" aria-label={copy.nav.primaryLabel}>
          {primaryNavigation.map((item) => (
            <NavAnchor
              action={item.kind === "action"}
              className={item.kind === "action" ? "desktop-nav__cta" : undefined}
              href={item.href}
              key={item.href}
            >
              {item.label}
            </NavAnchor>
          ))}
        </nav>

        <div className="mobile-nav">
          <button
            aria-controls="mobile-navigation-drawer"
            aria-expanded={isMenuOpen}
            aria-label={copy.nav.openMenu}
            className="mobile-nav__toggle"
            onClick={() => setIsMenuOpen(true)}
            ref={openButtonRef}
            type="button"
          >
            <Menu aria-hidden="true" strokeWidth={2.25} />
          </button>

          <div
            aria-hidden={!isMenuOpen}
            aria-label={copy.nav.menuDialog}
            aria-modal="true"
            className="mobile-nav__drawer"
            data-open={isMenuOpen}
            id="mobile-navigation-drawer"
            ref={drawerRef}
            role="dialog"
          >
            <div className="mobile-nav__drawer-header">
              <Link
                aria-label={copy.nav.brandLabel}
                className="mobile-nav__drawer-brand"
                href={localePath(locale, "/")}
                onClick={() => closeMenu(false)}
                tabIndex={isMenuOpen ? 0 : -1}
              >
                <BrandLockup appearance="reverse" />
              </Link>
              <button
                aria-label={copy.nav.closeMenu}
                className="mobile-nav__close"
                onClick={() => closeMenu()}
                ref={closeButtonRef}
                tabIndex={isMenuOpen ? 0 : -1}
                type="button"
              >
                <X aria-hidden="true" strokeWidth={2.25} />
              </button>
            </div>

            <nav aria-label={copy.nav.mobileLabel} className="mobile-nav__links">
              {primaryNavigation.map((item) => (
                <NavAnchor
                  action={item.kind === "action"}
                  className={item.kind === "action" ? "mobile-nav__cta" : undefined}
                  href={item.href}
                  key={item.href}
                  onClick={() => closeMenu(false)}
                  tabIndex={isMenuOpen ? 0 : -1}
                >
                  <span>{item.label}</span>
                  <ArrowUpRight aria-hidden="true" strokeWidth={2} />
                </NavAnchor>
              ))}
            </nav>

            <LanguageSwitcher
              locale={locale}
              onNavigate={() => closeMenu(false)}
              tabIndex={isMenuOpen ? 0 : -1}
              variant="drawer"
            />

            <p className="mobile-nav__message">{copy.nav.statement}</p>
          </div>
        </div>
      </div>
    </header>
  );
}
